import { useState, useMemo } from 'react';

import { getDefaultDateRange } from '../pages/BillingPageHelpers';
import {
  BillingSummary,
  ProjectGroup,
  PaginationInfo,
  CreditLedgerEntry,
  CreditLedgerDirection,
  CreditLedgerPaginationInfo,
} from '../services/billingService';
import { PaymentHistoryItem, OutstandingBalance } from '../services/paymentService';
import { Project } from '../services/projectService';

const useSummaryState = () => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  return { summary, setSummary, projectGroups, setProjectGroups, pagination, setPagination };
};

type TotalsType = { total_credits: number } | null;

const usePaymentState = () => {
  const [totals, setTotals] = useState<TotalsType>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [outstandingBalance, setOutstandingBalance] = useState<OutstandingBalance | null>(null);
  const [ledgerHistory, setLedgerHistory] = useState<CreditLedgerEntry[]>([]);
  const [ledgerPagination, setLedgerPagination] = useState<CreditLedgerPaginationInfo | null>(null);

  return { 
    totals, setTotals, paymentHistory, setPaymentHistory, 
    outstandingBalance, setOutstandingBalance,
    ledgerHistory, setLedgerHistory, ledgerPagination, setLedgerPagination,
  };
};

const useBillingDataState = () => ({ ...useSummaryState(), ...usePaymentState() });

const useUIState = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<string | null>(null);

  return { loading, setLoading, error, setError, paymentSuccess, setPaymentSuccess };
};

const useProjectFilterState = () => {
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  return {
    allProjects, setAllProjects, selectedProjectIds,
    setSelectedProjectIds, isFilterDropdownOpen, setIsFilterDropdownOpen,
  };
};

const useExpandState = () => {
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(getDefaultDateRange());
  const [ledgerDateRange, setLedgerDateRange] = useState<{ start: Date | null; end: Date | null }>(getDefaultDateRange());
  const [appliedDateRange, setAppliedDateRange] = useState<{ start: Date | null; end: Date | null }>(getDefaultDateRange());
  const [appliedLedgerDateRange, setAppliedLedgerDateRange] = useState<{ start: Date | null; end: Date | null }>(getDefaultDateRange());
  const [ledgerDirection, setLedgerDirection] = useState<CreditLedgerDirection>('all');
  const [appliedLedgerDirection, setAppliedLedgerDirection] = useState<CreditLedgerDirection>('all');

  return {
    expandedProjects, setExpandedProjects, currentPage, setCurrentPage,
    ledgerPage, setLedgerPage, dateRange, setDateRange,
    ledgerDateRange, setLedgerDateRange,
    appliedDateRange, setAppliedDateRange,
    appliedLedgerDateRange, setAppliedLedgerDateRange,
    ledgerDirection, setLedgerDirection,
    appliedLedgerDirection, setAppliedLedgerDirection,
  };
};

const useFilterState = () => ({ ...useProjectFilterState(), ...useExpandState() });

export const useBillingPageState = () => {
  const data = useBillingDataState();
  const ui = useUIState();
  const filter = useFilterState();

  return useMemo(() => ({ ...data, ...ui, ...filter }), [data, ui, filter]);
};
